<template>
  <b-modal
    centered
    hide-footer
    no-close-on-esc
    no-close-on-backdrop
    v-model="showModal"
    @close="closeModal"
    class="pa-4"
    modal-class="share-file-modal"
  >
    <template #modal-title>
      <h3 class="font-weight-bold">Share</h3>
    </template>
    <b-card
      no-body
      v-cloak
      class="rounded-0 border-0 mx-0 bg-light"
      @drop.prevent=""
      @dragover.prevent
    >
      <b-form @submit.prevent="submitFileShare">
        <b-card-text :style="'opacity: 1;'">
          <h6 class="font-weight-bold">Add an email to share your file</h6>
          <b-form-tags
            :key="shareTagsKey"
            v-model="form.emails"
            no-outer-focus
            tag-pills
            class="h-auto mb-2 form-control"
          >
            <template
              v-slot="{
                tags,
                inputAttrs,
                inputHandlers,
                disabled,
                addTag,
                removeTag,
              }"
            >
              <div class="mb-0">
                <div
                  class="mt-3"
                  style="font-size: 1.5rem"
                  :class="{
                    'd-inline-block': form.emails.length,
                    'd-none': !form.emails.length,
                  }"
                >
                  <b-form-tag
                    class="mr-1 mw-100 badge d-inline-flex align-items-baseline badge-pill border-gray"
                    v-for="(tag, i) in tags"
                    :key="i"
                    :title="tag"
                    :tag-pills="true"
                    :variant="'light'"
                    :disabled="loading"
                    :class="{
                      'border-danger':
                        checkInvalidTag(tag) ||
                        checkExcessTag(tag) ||
                        form.$hasError(`emails.${i}`),
                    }"
                    @remove="removeTag(tag)"
                  >
                    {{ tag }}
                  </b-form-tag>
                </div>
                <b-form-input
                  v-bind="inputAttrs"
                  v-on="inputHandlers"
                  autocomplete="on"
                  placeholder="Enter email..."
                  aria-describedby="shareFileFeedback"
                  class="border-0 p-0 mb-0"
                  :disabled="disabled || loading"
                  @keydown.enter.native="addTag()"
                ></b-form-input>
              </div>
            </template>
          </b-form-tags>
          <b-form-invalid-feedback
            v-show="tagErrors.message"
            id="shareFileFeedback"
            :class="{ 'd-block': tagErrors.message }"
          >
            {{ tagErrors.message }}
          </b-form-invalid-feedback>
          <b-form-invalid-feedback
            v-show="form.$getError('emails')"
            id="shareFileFeedback"
            :class="{ 'd-block': form.$getError('emails') }"
          >
            {{ form.$getError('emails') }}
          </b-form-invalid-feedback>

          <div class="ml-2">
            <b-icon class="mt-2" icon="exclamation-circle" variant="dark" />
            <span class="ml-1 mb-3">
              Press Enter on your keyboard to add an email address
            </span>
          </div>

          <div class="d-inline-block mt-3" style="font-size: 1.5rem">
            <h6 class="font-weight-bold">Currently shared with</h6>
            <b-form-tag
              class="mr-1 mw-100 badge d-inline-flex align-items-baseline badge-secondary badge-pill"
              v-for="(tag, i) in currentlySharedWith"
              :key="i"
              :title="tag"
              :tag-pills="true"
              :variant="'secondary'"
              :disabled="loading"
              :class="{
                'border-warning': checkInvalidTag(tag) || checkExcessTag(tag),
              }"
              @remove="confirmRevoke(tag)"
            >
              {{ tag }}
            </b-form-tag>
          </div>
        </b-card-text>

        <div class="my-3 my-md-4 mx-0">
          <b-container>
            <b-row class="justify-content-md-end">
              <b-col cols="6" md="auto" class="mb-md-0 mb-3 pr-1">
                <b-button
                  class="share-file-action-btn btn-outlined bg-white text-dark"
                  variant="outline-secondary"
                  :disabled="loading"
                  @click="clear"
                >
                  Clear
                </b-button>
              </b-col>
              <b-col cols="6" md="auto" class="mb-md-0 mb-3 pl-1">
                <b-button
                  type="submit"
                  class="share-file-action-btn"
                  variant="primary"
                  :loading="loading"
                  :disabled="loading || !form.emails.length"
                >
                  Share
                </b-button>
              </b-col>
            </b-row>
          </b-container>
        </div>
      </b-form>
    </b-card>
    <b-modal v-model="showSubscribeModal" centered title="File sharing failed">
      <p class="my-4">
        You have reached your file sharing capacity. Subscribe to premium to
        share files to more people.
      </p>

      <template #modal-footer>
        <b-button variant="secondary" @click="showSubscribeModal = false">
          Go Back
        </b-button>
        <b-button
          variant="primary"
          @click="$router.replace('/premium/subscription')"
        >
          Subscribe now
        </b-button>
      </template>
    </b-modal>
    <b-modal centered v-model="revokeConfirmShow" title="Cancel this share?">
      <p class="my-2">
        {{ pendingRevokeEmail }} will lose access to this file.
      </p>

      <template #modal-footer>
        <b-button variant="secondary" @click="cancelRevokeConfirm">
          No
        </b-button>
        <b-button variant="primary" @click="confirmedRevoke"> Yes </b-button>
      </template>
    </b-modal>
  </b-modal>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { each, remove } from 'lodash'
import Form from '@/utils/form'

export default {
  props: {
    showShareFiles: {
      type: Boolean,
      default: false,
    },
    selectedFiles: {
      type: Array,
    },
    fileShares: {
      type: Array,
    },
    /** When true, submit via the single-file /reshare endpoint (for a file
     * shared to you) instead of the bulk multi-file-share (owned files only). */
    isReshare: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      form: new Form({
        emails: [],
      }),
      currentlySharedWith: [],
      validTags: [],
      invalidTags: [],
      maxTags: [],
      tagErrors: {
        message: null,
        error_code: null,
      },
      search: '',
      showSubscribeModal: false,
      shareTagsKey: 0,
      revokeConfirmShow: false,
      pendingRevokeEmail: null,
    }
  },

  computed: {
    ...mapState({
      user: (state) => state.auth.user,
    }),
    isTradieUser() {
      return this.user ? this.user.user_type === 'tradie' : false
    },
    userSubcEmailLimit() {
      let limit = 0

      if (this.isTradieUser) {
        if (this.user.is_subscribed) {
          limit = 5
        } else {
          limit = 1
        }
      } else {
        if (this.user.is_subscribed) {
          limit = 50
        } else {
          limit = 5
        }
      }

      return limit
    },
    showModal: {
      get() {
        return this.showShareFiles
      },
      set() {
        this.$emit('closeShareFileModal')
      },
    },
  },

  methods: {
    ...mapActions({
      sendFileShare: 'fileShare/sendFileShare',
      reshareFile: 'fileShare/reshareFile',
      revokeFileShareEmail: 'fileShare/revokeFileShareEmail',
    }),
    closeModal() {
      this.clear()
      this.showModal = false
      this.$emit('close')
    },
    clearShareFileEmails() {
      this.form.$reset()
    },
    clearShareFileErrrorEmails() {
      this.invalidTags = []
      this.maxTags = []
      this.tagErrors = {
        message: null,
        error_code: null,
      }
    },
    clearShareInputField() {
      this.form.emails = []
      this.shareTagsKey += 1
    },
    clear() {
      this.clearShareFileEmails()
      this.clearShareFileErrrorEmails()
      this.clearShareInputField()
    },
    checkInvalidTag(tag) {
      return this.invalidTags.includes(tag)
    },
    checkExcessTag(tag) {
      return this.maxTags.includes(tag)
    },
    async submitFileShare() {
      this.loading = true
      this.form.$busy = true
      this.clearShareFileErrrorEmails()

      const shareRequest = this.isReshare
        ? this.reshareFile({
            fileId: this.selectedFiles[0],
            emails: this.form.emails,
          })
        : this.sendFileShare({
            fileIds: this.selectedFiles, // array of file IDs
            emails: this.form.emails,
          })

      await shareRequest
        .then((res) => {
          this.$emit('fileShareResponsePrompt', res)
          this.clear()
          this.closeModal()
        })
        .catch((error) => {
          this.form.$handleError(error)
          this.$emit('fileShareResponsePrompt', error.response)
        })
        .finally(() => {
          this.loading = false
          this.form.$busy = false
        })
    },
    confirmRevoke(email) {
      this.pendingRevokeEmail = email
      this.revokeConfirmShow = true
    },
    cancelRevokeConfirm() {
      this.revokeConfirmShow = false
      this.pendingRevokeEmail = null
    },
    async confirmedRevoke() {
      const email = this.pendingRevokeEmail
      this.revokeConfirmShow = false
      this.pendingRevokeEmail = null
      await this.currentlySharedRevoke(email)
    },
    async currentlySharedRevoke(email) {
      this.loading = true
      this.clearShareFileErrrorEmails()

      let formData = new FormData()
      formData.append(`email`, email)

      let data = {
        formData: formData,
        fileId: this.selectedFiles[0],
      }

      await this.revokeFileShareEmail(data)
        .then(() => {
          remove(this.currentlySharedWith, (e) => {
            return e === email
          })

          // fileShares is the same array reference the parent read off its
          // underlying file object (not a copy) — splicing it here removes
          // the share everywhere that data is read from, immediately,
          // instead of waiting on a list refetch to catch up.
          remove(this.fileShares, (share) => {
            return share.shared_to && share.shared_to.email === email
          })

          this.$emit('reloadFileList')
        })
        .catch((error) => {
          this.tagErrors.error_code = error.response.data.error_code
          this.tagErrors.message = error.response.data.message

          this.$emit('fileShareResponsePrompt', error.response)
        })
        .finally(() => {
          this.loading = false
        })
    },
  },

  watch: {
    fileShares(newVal, oldVal) {
      this.currentlySharedWith = []

      if (newVal.length) {
        const myId = this.user && this.user.id
        each(newVal, (sharedFile) => {
          // Only shares this user made are theirs to revoke here — the
          // revoke endpoint requires shared_by === current user, and this
          // list can include shares other people made (e.g. the original
          // owner, or another recipient who reshared it) when reopened from
          // a reshared file.
          const sharedById = sharedFile.shared_by && sharedFile.shared_by.id
          if (sharedFile.shared_to && sharedById === myId) {
            this.currentlySharedWith.push(sharedFile.shared_to.email)
          }
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.badge-secondary {
  color: #364052;
  background: #e8eaed;
}

/deep/ .border-gray {
  border-color: #e5e5e5;
}

/deep/ .b-form-tags-button {
  display: none;
}

// Clear (btn-outlined, padding: 14px) and Share (plain .btn, padding: 4px
// 32px) inherit different vertical padding from their own variant classes.
// With a fixed height that doesn't matter for the outer box, but it does
// shift where the text sits inside it — flex-centring content here (and
// zeroing the vertical padding that was fighting it) guarantees both
// buttons' labels land on the exact same centreline regardless of variant.
.share-file-action-btn {
  height: 64px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  // !important because .btn-outlined (a more specific .btn.btn-outlined
  // selector) sets its own padding: 14px on all sides, which would
  // otherwise win over this and reintroduce the mismatch.
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  // Full-width on phones (columns are cols="6", side by side); from md up
  // the columns are auto-width (md="auto") so the pair sits compact and
  // right-aligned instead of stretched apart by a spacer column — give
  // the buttons themselves a sensible minimum instead of stretching edge
  // to edge of their now content-sized column.
  width: 100%;

  @media (min-width: 768px) {
    width: auto;
    min-width: 130px;
  }

  @media (max-width: 575px) {
    height: 48px !important;
  }
}

/deep/ .share-file-modal {
  .modal-dialog {
    max-width: 708px;
  }

  @media (max-width: 575px) {
    // Was `margin: 0.5rem auto` — a fixed vertical margin fights the
    // `centered` prop's own flex-based vertical centering, pinning the
    // dialog right at the top of the screen instead of the middle (the
    // Clear/Share buttons then land wherever the (short) content happens
    // to end, well above the screen's actual centre). Only constrain the
    // horizontal inset here; let bootstrap-vue's own centered-modal
    // flexbox handle vertical placement like it does at every other size.
    .modal-dialog {
      max-width: calc(100% - 1rem);
      margin-left: auto;
      margin-right: auto;
    }

    .modal-content {
      border-radius: 0.75rem;
    }

    .modal-header {
      padding: 1rem 1.25rem 0.5rem;
    }

    .modal-header h3 {
      font-size: 1.25rem;
      margin-bottom: 0;
    }

    .modal-body {
      padding: 0.75rem 1.25rem 1.25rem;
    }
  }
}
</style>
